import changeSwipeGameIcon from '../../entities/changeSwipeGameIcon';
import findGameInTheList from '../../entities/findGameInTheList';
import playGame from '../../features/playGames';
import eventType from '../enums/eventType';
import { LocalStoreKeys } from '../enums/localStoreKeys';
import eventEmitters from '../lib/eventEmitters';
import { getSDKConfig } from '../lib/getSDKConfig';
import { setLocalStore } from '../lib/localStore';
import { SwipeGamesItem } from '../model/swipe';
import { SelectedGame } from '../types/SwipeSDKConfigType';
import { createIframe } from './createIframe';
import { GameIconManager } from './gameIconManager';
import getElementSelectors from '../lib/getElementSelectors';
import logger from '../lib/logger';
import { applyGameGradient } from '../../features/applyGameGradient';
import { updateGameViewportHeight } from '../lib/updateGameViewportHeight';
import getCurrentGameSelected from '../lib/currentGameSelected';

export class SwipeMenuV2 {
  private static readonly SCROLL_DELAY_MS = 50;
  private static readonly INITIAL_SCROLL_DELAY_MS = 50;

  private container: HTMLElement | null = null;
  private icons: SwipeGamesItem[] = [];
  private gameIconManager: GameIconManager | null = null;
  private gameShowcaseTimeout: ReturnType<typeof setTimeout> | null = null;
  private gameShowcaseSequenceId = 0;
  private containerId = 'scrollable-icons';
  private menuLoaded = false;

  public init(containerId = 'scrollable-icons'): void {
    this.containerId = containerId;
    const initFn = () => this.prepare();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initFn);
    } else {
      initFn();
    }
  }

  private prepare(): void {
    this.removeDropdown();
    this.setupSwipeControlResizeListener();
    applyGameGradient(getSDKConfig()?.swipeGames?.iconPosition || undefined);
  }

  public loadMenu(): void {
    if (this.menuLoaded) return;

    this.container = this.findContainer(this.containerId);
    if (!this.container) return;

    this.menuLoaded = true;
    this.icons = getSDKConfig()?.swipeGames?.gameListConfig?.gameList || [];

    try {
      this.initializeManagers();
      this.render();
    } catch (error) {
      logger.error('SwipeMenuV2: failed to build menu on swipe-open', {
        error,
      });
    }
  }

  private removeDropdown(): void {
    const { dropdown, dropDownOverlay } = getElementSelectors();
    dropdown?.remove();
    dropDownOverlay?.remove();
  }

  private findContainer(containerId: string): HTMLElement | null {
    return (
      document.querySelector(`.${containerId}`) ||
      document.getElementById(containerId)
    );
  }

  private initializeManagers(): void {
    this.gameIconManager = new GameIconManager(this.container, this.icons);

    this.gameIconManager.setOnIconClick((icon, index) =>
      this.handleIconClick(icon, index),
    );
  }

  private render(): void {
    if (!this.container || !this.gameIconManager) return;

    this.gameIconManager.setDefaultActiveIcon();
    this.updateMenuAction();
    this.gameIconManager.renderAllIcons();
    this.gameIconManager.updateIconVisibility();

    const activeIndex = this.gameIconManager.getActiveIconIndex();
    if (activeIndex !== -1) {
      setTimeout(() => {
        this.gameIconManager?.scrollIconIntoView(activeIndex);
      }, SwipeMenuV2.INITIAL_SCROLL_DELAY_MS);
    }
  }

  public setIcons(icons: SwipeGamesItem[]): void {
    this.icons = icons;

    if (this.container) {
      this.initializeManagers();
    }

    this.render();
  }

  private getVariant(): string {
    const container = document.getElementById('swipe-sdk-container');
    const attr = container?.getAttribute('data-swipe-variant');
    if (attr) return attr;
    return (
      ((window as unknown as Record<string, unknown>)
        .__SWIPE_VARIANT__ as string) || 'v3'
    );
  }

  private updateMenuAction(): void {
    this.setupDrawerMenu();
    this.setupSideDrawer();
  }

  private setupSideDrawer(): void {
    const hamburger = document.getElementById('sdk-drawer-menu-hamburger');
    const drawer = document.getElementById('swipe-sdk-drawer');
    const closeBtn = document.getElementById('close-drawer-btn');
    const overlay = document.querySelector('.drawer-overlay');
    const grid = document.querySelector('.game-grid-drawer');
    const countLabel = document.getElementById('swipe-game-counts-drawer');

    if (!hamburger || !drawer || !grid) return;

    const markActiveGame = () => {
      const currentGameId = getCurrentGameSelected()?.currentGameId?.toString();
      for (const el of Array.from(grid.querySelectorAll('.game-item'))) {
        el.classList.toggle(
          'active',
          el.getAttribute('data-game-id') === currentGameId,
        );
      }
    };

    grid.innerHTML = this.icons
      .map(
        (game, index) => `
      <div class="game-item" data-game-index="${index}" data-game-id="${game.gameId}">
        <img src="${game.gameIconUrl}" alt="${game.displayName || 'Game'}">
        <span>${game.displayName || 'Game'}</span>
      </div>
    `,
      )
      .join('');

    if (countLabel) {
      countLabel.textContent = `${this.icons.length} games`;
    }

    for (const item of Array.from(grid.querySelectorAll('.game-item'))) {
      item.addEventListener('click', async () => {
        const gameIndex = parseInt(
          item.getAttribute('data-game-index') || '0',
          10,
        );
        const gameId = item.getAttribute('data-game-id') || '';

        closeSideDrawer();
        this.setActiveIcon(gameIndex);
        for (const el of Array.from(grid.querySelectorAll('.game-item'))) {
          el.classList.remove('active');
        }
        item.classList.add('active');

        if (gameId) {
          await this.handleGameSelection(
            gameId,
            eventType.SWIPE_DRAWER_GAMECHANGE,
          );
        }
      });
    }

    markActiveGame();

    const openSideDrawer = () => {
      markActiveGame();
      drawer.classList.remove('closing');
      drawer.classList.add('open');
      overlay?.classList.add('open');
      eventEmitters(eventType.SWIPE_DRAWER_OPEN);
    };

    const closeSideDrawer = () => {
      drawer.classList.remove('open');
      drawer.classList.add('closing');
      overlay?.classList.remove('open');
      setTimeout(() => {
        drawer.classList.remove('closing');
      }, 300);
      eventEmitters(eventType.SWIPE_DRAWER_CLOSED);
    };

    hamburger.addEventListener('click', openSideDrawer);
    closeBtn?.addEventListener('click', closeSideDrawer);
    overlay?.addEventListener('click', closeSideDrawer);
  }

  public collapseNav(): void {}

  private setupDrawerMenu(): void {
    const menu = document.getElementById('sdk-drawer-menu');
    const handle = document.getElementById('sdk-drawer-menu-handle');

    if (!menu || !handle) return;

    const variant = this.getVariant();

    menu.classList.add(`sdk-drawer-menu--${variant}`);
    menu.classList.add('open');

    updateGameViewportHeight();

    const toggleMenu = () => {
      const isOpen = menu.classList.contains('open');

      if (isOpen) {
        menu.classList.remove('open');
      } else {
        menu.classList.add('open');
      }

      updateGameViewportHeight();

      eventEmitters(
        !isOpen ? eventType.SWIPE_DRAWER_OPEN : eventType.SWIPE_DRAWER_CLOSED,
      );

      if (isOpen) {
        this.startGameShowcase(handle);
      } else {
        this.stopGameShowcase();
      }
    };

    handle.addEventListener('click', toggleMenu);
  }

  private startGameShowcase(handle: HTMLElement): void {
    const label = document.getElementById('sdk-drawer-menu-handle-label');
    const icon = document.getElementById(
      'sdk-drawer-menu-handle-icon',
    ) as HTMLImageElement | null;
    if (!label || this.icons.length === 0) return;

    this.stopGameShowcase();

    const currentGameId = getCurrentGameSelected()?.currentGameId;
    const otherGames = this.icons.filter(
      (game) => game.gameId?.toString() !== currentGameId?.toString(),
    );
    if (otherGames.length === 0) return;

    this.gameShowcaseSequenceId += 1;
    const sequenceId = this.gameShowcaseSequenceId;

    const MORE_GAMES_TEXT = 'More Games';
    const PLAY_PREFIX = 'Play ';
    const TYPE_INTERVAL_MS = 65;
    const ERASE_INTERVAL_MS = 40;
    const HOLD_MS = 2600;
    const GAP_MS = 15000;

    type ShowcasePhase = { prefix: string; name: string; iconUrl?: string };

    const phases: ShowcasePhase[] = otherGames.flatMap((game) => [
      { prefix: '', name: MORE_GAMES_TEXT },
      {
        prefix: PLAY_PREFIX,
        name: game.displayName || 'Game',
        iconUrl: game.gameIconUrl,
      },
    ]);

    let phaseIndex = 0;

    const showNextPhase = () => {
      if (sequenceId !== this.gameShowcaseSequenceId) return;

      const phase = phases[phaseIndex];
      const { prefix, name } = phase;
      let charIndex = 0;

      if (icon) {
        if (phase.iconUrl) {
          icon.src = phase.iconUrl;
          icon.style.removeProperty('display');
        } else {
          icon.removeAttribute('src');
          icon.style.display = 'none';
        }
      }
      label.textContent = prefix;
      handle.classList.add('teasing', 'typing');

      const typeChar = () => {
        if (sequenceId !== this.gameShowcaseSequenceId) return;

        charIndex++;
        label.textContent = prefix + name.slice(0, charIndex);

        if (charIndex < name.length) {
          this.gameShowcaseTimeout = setTimeout(typeChar, TYPE_INTERVAL_MS);
        } else {
          handle.classList.remove('typing');
          this.gameShowcaseTimeout = setTimeout(eraseName, HOLD_MS);
        }
      };

      const eraseName = () => {
        if (sequenceId !== this.gameShowcaseSequenceId) return;

        handle.classList.add('typing');
        charIndex--;
        label.textContent = prefix + name.slice(0, charIndex);

        if (charIndex > 0) {
          this.gameShowcaseTimeout = setTimeout(eraseName, ERASE_INTERVAL_MS);
        } else {
          handle.classList.remove('teasing', 'typing');
          if (icon) icon.removeAttribute('src');
          phaseIndex = (phaseIndex + 1) % phases.length;
          this.gameShowcaseTimeout = setTimeout(showNextPhase, GAP_MS);
        }
      };

      typeChar();
    };

    this.gameShowcaseTimeout = setTimeout(showNextPhase, GAP_MS);
  }

  private stopGameShowcase(): void {
    this.gameShowcaseSequenceId += 1;

    if (this.gameShowcaseTimeout) {
      clearTimeout(this.gameShowcaseTimeout);
      this.gameShowcaseTimeout = null;
    }

    const handle = document.getElementById('sdk-drawer-menu-handle');
    const label = document.getElementById('sdk-drawer-menu-handle-label');
    const icon = document.getElementById(
      'sdk-drawer-menu-handle-icon',
    ) as HTMLImageElement | null;
    handle?.classList.remove('teasing', 'typing');
    if (label) label.textContent = '';
    if (icon) icon.removeAttribute('src');
  }

  private async handleGameSelection(
    gameId: string,
    event: string,
  ): Promise<void> {
    const foundGame = findGameInTheList(gameId);

    const currentGameselected: SelectedGame = {
      currentGameId: gameId,
    };
    changeSwipeGameIcon(currentGameselected);
    setLocalStore(LocalStoreKeys.SELECTED_GAME, currentGameselected);

    const gameLaunchUrl = foundGame ? await playGame() : null;
    if (gameLaunchUrl) {
      createIframe(gameLaunchUrl);
    }
    eventEmitters(event);
  }

  private async handleIconClick(
    icon: SwipeGamesItem,
    index: number,
  ): Promise<void> {
    this.setActiveIcon(index);
    this.closeDrawerMenu();

    const gameId = icon.gameId?.toString();

    if (gameId) {
      await this.handleGameSelection(gameId, eventType.SWIPE_TOPBAR_GAMECHANGE);
    }
  }

  public setActiveIcon(index: number): void {
    if (!this.gameIconManager?.isValidIndex(index)) return;

    const wasChanged = this.gameIconManager.getActiveIconIndex() !== index;
    if (!wasChanged) return;

    this.gameIconManager.setActiveIconIndex(index);
    this.gameIconManager.updateIconVisibility();

    setTimeout(() => {
      this.gameIconManager?.scrollIconIntoView(index);
    }, SwipeMenuV2.SCROLL_DELAY_MS);
  }

  private closeDrawerMenu(): void {
    const menu = document.getElementById('sdk-drawer-menu');

    if (menu?.classList.contains('open')) {
      menu.classList.remove('open');
      updateGameViewportHeight();
    }
  }

  public getActiveIcon(): SwipeGamesItem | null {
    return this.gameIconManager?.getActiveIcon() ?? null;
  }

  public clearActiveIcon(): void {
    if (!this.gameIconManager) return;

    this.gameIconManager.setActiveIconIndex(-1);
    this.gameIconManager.updateIconVisibility();
  }

  private setupSwipeControlResizeListener(): void {
    const { swipe_control } = getElementSelectors();

    if (swipe_control) {
      window.addEventListener('resize', () => {
        const swipeYPos = sessionStorage.getItem('swipeYPos');
        if (swipeYPos) {
          swipe_control.style.top = `${Number(swipeYPos)}%`;
        }
        updateGameViewportHeight();
      });
    }
  }
}

export const swipeMenusV2 = new SwipeMenuV2();
